// import axios from 'axios'; // Will be needed for real API calls

class GoogleBusinessService {
  constructor() {
    this.accessToken = null;
  }

  async initializeAuth() {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    
    // Check if Google Client ID is properly configured
    if (!clientId || clientId === 'your_google_client_id_here') {
      throw new Error('Google Client ID not configured. Please set REACT_APP_GOOGLE_CLIENT_ID in your .env file.');
    }
    
    const redirectUri = `${window.location.origin}/auth/google/callback`;
    
    // Set required scopes for Google My Business
    const scopes = [
      'https://www.googleapis.com/auth/business.manage',
      'https://www.googleapis.com/auth/userinfo.profile',
      'https://www.googleapis.com/auth/userinfo.email',
      'https://www.googleapis.com/auth/content',
      'https://www.googleapis.com/auth/adwords',
      'https://www.googleapis.com/auth/analytics.readonly',
      'https://www.googleapis.com/auth/youtube.readonly',
      'https://www.googleapis.com/auth/webmasters.readonly'
    ].join(' ');

    const state = this.generateState();
    localStorage.setItem('google_oauth_state', state);
    
    const authUrl = `https://accounts.google.com/oauth2/authorize?` +
      `client_id=${clientId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `scope=${encodeURIComponent(scopes)}&` +
      `response_type=code&` +
      `access_type=offline&` +
      `prompt=consent&` +
      `state=${state}&` +
      `include_granted_scopes=true`;

    return {
      authUrl,
      clientId,
      redirectUri,
      state
    };
  }

  generateState() {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  async handleAuthCallback(code, state) {
    try {
      const storedState = localStorage.getItem('google_oauth_state');
      if (state && state !== storedState) {
        throw new Error('Invalid state parameter');
      }
      
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      const clientSecret = process.env.REACT_APP_GOOGLE_CLIENT_SECRET;
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      
      if (!clientSecret) {
        console.warn('Google OAuth requires backend service for token exchange. Using mock data.');
        
        const mockTokens = {
          access_token: 'mock_access_token_' + Date.now(),
          refresh_token: 'mock_refresh_token',
          expires_in: 3600,
          token_type: 'Bearer',
          scope: 'mock_scope',
          obtained_at: Date.now()
        };
        
        this.accessToken = mockTokens.access_token;
        localStorage.setItem('google_tokens', JSON.stringify(mockTokens));
        localStorage.removeItem('google_oauth_state');
        
        return mockTokens;
      }
      
      // Real token exchange (requires backend in production)
      const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          code: code,
          client_id: clientId,
          client_secret: clientSecret,
          redirect_uri: redirectUri,
          grant_type: 'authorization_code'
        })
      });
      
      if (!tokenResponse.ok) {
        throw new Error('Failed to exchange code for tokens');
      }
      
      const tokens = await tokenResponse.json();
      tokens.obtained_at = Date.now();
      
      this.accessToken = tokens.access_token;
      localStorage.setItem('google_tokens', JSON.stringify(tokens));
      localStorage.removeItem('google_oauth_state');
      
      return tokens;
    } catch (error) {
      console.error('Google Auth callback error:', error);
      throw error;
    }
  }

  async getBusinessLocations() {
    try {
      if (!this.accessToken) {
        // Check for stored tokens
        const storedTokens = localStorage.getItem('google_tokens');
        if (storedTokens) {
          const tokens = JSON.parse(storedTokens);
          this.accessToken = tokens.access_token;
        } else {
          throw new Error('Google access token not found');
        }
      }

      // For demo purposes, return mock business data
      // In production, this would make API calls to Google My Business API
      console.warn('Using mock Google Business data. Requires backend service for real API calls.');
      
      const mockLocations = [{
        name: 'accounts/123/locations/456',
        title: 'Demo Business (Google)',
        storefrontAddress: {
          addressLines: ['123 Main St'],
          locality: 'New York',
          administrativeArea: 'NY',
          postalCode: '10001'
        },
        primaryPhone: '+1 (555) 123-4567',
        websiteUri: 'https://demo-business.com',
        primaryCategory: {
          displayName: 'Restaurant'
        },
        profile: {
          description: 'Demo business from Google My Business'
        },
        latlng: {
          latitude: 40.7128,
          longitude: -74.0060
        },
        metadata: {
          averageRating: 4.5,
          newReviewCount: 150
        },
        regularHours: {
          periods: [
            {
              openDay: 1,
              openTime: '09:00',
              closeTime: '17:00'
            }
          ]
        }
      }];

      return this.formatBusinessData(mockLocations);
    } catch (error) {
      console.error('Error fetching business locations:', error);
      throw error;
    }
  }

  formatBusinessData(locations) {
    return locations.map(location => ({
      id: location.name,
      name: location.title || 'Unknown Business',
      address: this.formatAddress(location.storefrontAddress),
      phone: location.primaryPhone || '',
      website: location.websiteUri || '',
      category: location.primaryCategory?.displayName || 'Business',
      description: location.profile?.description || '',
      position: {
        lat: location.latlng?.latitude || 0,
        lng: location.latlng?.longitude || 0,
      },
      rating: location.metadata?.averageRating || 0,
      reviewCount: location.metadata?.newReviewCount || 0,
      photos: location.profile?.photos || [],
      hours: this.formatHours(location.regularHours),
      source: 'google',
    }));
  }

  formatAddress(address) {
    if (!address) return '';
    
    const parts = [
      address.addressLines?.join(' '),
      address.locality,
      address.administrativeArea,
      address.postalCode,
    ].filter(Boolean);
    
    return parts.join(', ');
  }

  formatHours(hours) {
    if (!hours || !hours.periods) return {};
    
    const formatted = {};
    const dayNames = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
    
    hours.periods.forEach(period => {
      const day = dayNames[period.openDay];
      if (day) {
        formatted[day.toLowerCase()] = {
          open: period.openTime || '00:00',
          close: period.closeTime || '23:59',
          closed: !period.openTime,
        };
      }
    });
    
    return formatted;
  }

  async refreshTokens() {
    try {
      const storedTokens = localStorage.getItem('google_tokens');
      if (!storedTokens) {
        throw new Error('No tokens found');
      }

      // In production, this would refresh the token via backend service
      console.warn('Token refresh requires backend service. Using existing token.');
      const tokens = JSON.parse(storedTokens);
      this.accessToken = tokens.access_token;
      return tokens;
    } catch (error) {
      console.error('Error refreshing tokens:', error);
      this.clearTokens();
      throw error;
    }
  }

  clearTokens() {
    localStorage.removeItem('google_tokens');
    localStorage.removeItem('google_oauth_state');
    this.accessToken = null;
  }

  isAuthenticated() {
    const tokens = localStorage.getItem('google_tokens');
    if (!tokens) return false;
    
    try {
      const parsed = JSON.parse(tokens);
      // Check if token is expired
      if (parsed.obtained_at && parsed.expires_in) {
        const expiresAt = parsed.obtained_at + (parsed.expires_in * 1000);
        return Date.now() < expiresAt;
      }
      return !!parsed.access_token;
    } catch (e) {
      return false;
    }
  }
}

export default GoogleBusinessService;